// Internal modules
import { appStore } from '@/store';
import { $logger, features } from '@/lib';
import { fetchAndResetContent } from '@/lib/content';
import { getGlobals, getLocale, updateLocale } from '@/lib/globals';
import { redirectToGenericError, redirectToXoon } from '@/lib/utils';
import {
  BATCHED_QUERY_OPTIONS_OVERRIDES,
  LITE_INELIGIBLE_COUNTRIES,
} from './constants';
import {
  queryCookieBanner,
  removeCookieBanner,
  setCookieBanner,
} from '../../shared';
import {
  getSuggestedDefaultLanguageForCountry,
  queryInitialData,
  readSuggestedLanguagesQuery,
} from './graphql';
import {
  logCountryChangeAttemptToFPTI,
  logCountryChangeErrorToFPTI,
  logCountryChangeSuccessToFPTI,
} from './fpti';
import {
  cacheInitialDataInReduxStore,
  fetchCheckoutSessionData,
  fetchLocaleMetadata,
} from '@/components/App/api';
import { conditionallyCalculateShippingAndTaxes } from '@/components/Shipping/utils';

/**
 * When the user changes country we need to do the following:
 * 1. Remove the cookie banner if it is present
 * 2. Toggle the page loading spinner, which will be shown while
 *    we fetch all the necessary new data
 * 3. Get the suggested default language for the new country. This is
 *    needed to fetch the correct content as well as the correct
 *    locale metadata.
 * 4. Update the locale - this needs to be done before we make any API
 *    calls so that the correct country and language headers are set
 *    on the API requests. The checkoutSession request relies on the
 *    resolved locale for formatting the cart amounts, not an argument
 *    passed to the query. The cookie banner also relies on the locale
 *    instead of an argument.
 * 5. Fetch new data
 *    CDN:
 *      - content for new country/language combo
 *    API:
 *      - Locale metadata for country/language
 *      - Cart amounts to make sure they are correctly formatted
 *      - Cookie banner - will only be shown if the user did not already
 *        accept a previously shown cookie banner.
 *      - Initial data (elmo experiments, checkout session flags, etc)
 * 6. Reset the store and the content, show the cookie banner if it is
 *    required and finally hide the loading spinner.
 */
const handleCountryChange = ({ fromCountry, toCountry }) => {
  // If the `blockNonDomesticPayer` flag is active then the user is not
  // allowed to change countries.
  if (features.isActive('blockNonDomesticPayer')) {
    return $logger.info('non_domestic_payer_country_change_blocked', {
      fromCountry,
      toCountry,
    });
  }

  removeCookieBanner();
  appStore.getActions().ui.toggleIsPageLoading(true);
  logCountryChangeAttemptToFPTI({ fromCountry, toCountry });

  const shouldRedirectToXoon =
    features.isNotActive('shouldHandleCountryChange') ||
    LITE_INELIGIBLE_COUNTRIES.includes(toCountry);
  if (!getGlobals().isInternalRequest && shouldRedirectToXoon) {
    const { language } = getLocale();
    return redirectToXoon({
      'country.x': toCountry,
      'locale.x': `${language}_${toCountry}`,
    });
  }

  const territories = readSuggestedLanguagesQuery();
  const suggestedDefaultLanguage = getSuggestedDefaultLanguageForCountry({
    countryCode: toCountry,
    territories,
  });

  updateLocale({
    country: toCountry,
    language: suggestedDefaultLanguage,
  });

  /**
   * @TO_DO @APOLLO_MIGRATION
   * When migrating to Apollo Client for state management switch to using
   * the checkoutSessionQuery from `lib/graphql`. I'm reusing this
   * query for now since the data needs to be saved in the redux store so it
   * can be accessed by components that need it.
   */
  const promises = [
    queryCookieBanner(),
    fetchLocaleMetadata(BATCHED_QUERY_OPTIONS_OVERRIDES),
    fetchCheckoutSessionData({
      fetchPolicy: 'network-only',
      ...BATCHED_QUERY_OPTIONS_OVERRIDES,
    }),
    fetchAndResetContent(),
    queryInitialData({
      countryCode: toCountry,
      languageCode: suggestedDefaultLanguage,
    }),
  ];
  return Promise.all(promises)
    .then(([cookieBanner, localeMetadata, checkoutSession]) => {
      logCountryChangeSuccessToFPTI({ fromCountry, toCountry });
      appStore.getActions().ui.toggleIsPageLoading(false);
      cacheInitialDataInReduxStore({
        checkoutSessionResponse: checkoutSession,
        localeMetadataResponse: localeMetadata,
      });
      setCookieBanner(cookieBanner);
    })
    .then(conditionallyCalculateShippingAndTaxes)
    .catch(error => {
      logCountryChangeErrorToFPTI({
        fromCountry,
        int_error_code: error.message,
        int_error_desc: 'country_change_contingency',
        toCountry,
      });
      redirectToGenericError();
    });
};

export { handleCountryChange };
